import { useState, useMemo } from 'react';
import {
  Download,
  Loader2,
  Search,
  Printer,
  Package,
  ShieldAlert,
  Calendar,
  User,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { cn } from '@/lib/utils';

interface InventoryVariance {
  product_id: string;
  product_name: string;
  product_sku: string | null;
  category_name: string | null;
  opening_foh: number;
  transfers_in: number;
  transfers_out: number;
  sales_qty: number;
  expected_closing: number;
  actual_closing: number | null;
  variance: number | null;
  variance_value: number | null;
  last_count_at: string | null;
  unit_cost: number | null;
}

interface StockOverride {
  id: string;
  product_id: string;
  product_name: string;
  order_id: string | null;
  cashier_name: string;
  manager_name: string;
  override_reason: string | null;
  foh_quantity_at_time: number;
  quantity_sold: number;
  created_at: string;
}

export default function CashierVarianceReport() {
  const { currentLocation } = useLocationStore();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Cashier Variance Report' });

  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [searchQuery, setSearchQuery] = useState('');
  const [varianceFilter, setVarianceFilter] = useState<'all' | 'variance' | 'shortage' | 'overage'>('all');
  const [selectedTab, setSelectedTab] = useState('variance');

  // Fetch inventory variance data
  const { data: varianceData, isLoading: varianceLoading } = useQuery({
    queryKey: ['cashier-variance', currentLocation?.id, dateFrom, dateTo],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      // Get inventory items with their FOH quantities
      const { data: inventoryItems, error: invError } = await supabase
        .from('inventory_items')
        .select(`
          id,
          product_id,
          quantity_foh,
          quantity_boh,
          quantity_on_hand,
          last_counted_at,
          product:products(
            id,
            name,
            sku,
            cost,
            category:categories(name)
          )
        `)
        .eq('location_id', currentLocation.id);

      if (invError) throw invError;

      // Get transfers in the date range
      const { data: transfers, error: transferError } = await supabase
        .from('inventory_transfers')
        .select('inventory_item_id, transfer_type, quantity')
        .eq('location_id', currentLocation.id)
        .gte('created_at', `${dateFrom}T00:00:00`)
        .lte('created_at', `${dateTo}T23:59:59`);

      if (transferError) throw transferError;

      // Get sales in the date range
      const { data: orderItems, error: salesError } = await supabase
        .from('order_items')
        .select(`
          product_id,
          quantity,
          order:orders!inner(
            id,
            location_id,
            status,
            completed_at
          )
        `)
        .eq('order.location_id', currentLocation.id)
        .eq('order.status', 'completed')
        .gte('order.completed_at', `${dateFrom}T00:00:00`)
        .lte('order.completed_at', `${dateTo}T23:59:59`);

      if (salesError) throw salesError;

      // Calculate variance for each product
      const varianceMap = new Map<string, InventoryVariance>();

      for (const item of inventoryItems || []) {
        const product = item.product as any;
        if (!product) continue;

        const productId = item.product_id;
        const currentFoh = item.quantity_foh ?? item.quantity_on_hand ?? 0;

        // Calculate transfers
        const productTransfers = (transfers || []).filter(
          t => t.inventory_item_id === item.id
        );
        const transfersIn = productTransfers
          .filter(t => t.transfer_type === 'boh_to_foh')
          .reduce((sum, t) => sum + Number(t.quantity), 0);
        const transfersOut = productTransfers
          .filter(t => t.transfer_type === 'foh_to_boh')
          .reduce((sum, t) => sum + Number(t.quantity), 0);

        // Calculate sales
        const productSales = (orderItems || [])
          .filter(oi => oi.product_id === productId)
          .reduce((sum, oi) => sum + oi.quantity, 0);

        // Calculate expected closing
        // Opening FOH = Current FOH + Sales - Transfers In + Transfers Out
        const openingFoh = currentFoh + productSales - transfersIn + transfersOut;
        const expectedClosing = openingFoh + transfersIn - transfersOut - productSales;

        // Variance = Current FOH - Expected Closing
        const variance = currentFoh - expectedClosing;
        const unitCost = product.cost ?? 0;

        varianceMap.set(productId, {
          product_id: productId,
          product_name: product.name,
          product_sku: product.sku,
          category_name: product.category?.name || null,
          opening_foh: openingFoh,
          transfers_in: transfersIn,
          transfers_out: transfersOut,
          sales_qty: productSales,
          expected_closing: expectedClosing,
          actual_closing: currentFoh,
          variance: variance,
          variance_value: variance * unitCost,
          last_count_at: item.last_counted_at,
          unit_cost: unitCost,
        });
      }

      return Array.from(varianceMap.values());
    },
    enabled: !!currentLocation?.id,
  });

  // Fetch stock override logs
  const { data: overrideLogs, isLoading: overridesLoading } = useQuery({
    queryKey: ['stock-overrides', currentLocation?.id, dateFrom, dateTo],
    queryFn: async () => {
      if (!currentLocation?.id) return [];

      const { data, error } = await supabase
        .from('stock_override_logs')
        .select(`
          id,
          product_id,
          order_id,
          override_reason,
          foh_quantity_at_time,
          quantity_sold,
          created_at,
          product:products(name),
          cashier:profiles!stock_override_logs_cashier_id_fkey(first_name, last_name),
          manager:profiles!stock_override_logs_manager_id_fkey(first_name, last_name)
        `)
        .eq('location_id', currentLocation.id)
        .gte('created_at', `${dateFrom}T00:00:00`)
        .lte('created_at', `${dateTo}T23:59:59`)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching override logs:', error);
        return [];
      }

      return (data || []).map((log: any) => ({
        id: log.id,
        product_id: log.product_id,
        product_name: log.product?.name || 'Unknown',
        order_id: log.order_id,
        cashier_name: log.cashier
          ? `${log.cashier.first_name || ''} ${log.cashier.last_name || ''}`.trim()
          : 'Unknown',
        manager_name: log.manager
          ? `${log.manager.first_name || ''} ${log.manager.last_name || ''}`.trim()
          : 'Unknown',
        override_reason: log.override_reason,
        foh_quantity_at_time: log.foh_quantity_at_time,
        quantity_sold: log.quantity_sold,
        created_at: log.created_at,
      })) as StockOverride[];
    },
    enabled: !!currentLocation?.id,
  });

  // Filter and search variance data
  const filteredVariance = useMemo(() => {
    if (!varianceData) return [];

    return varianceData.filter(item => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesName = item.product_name.toLowerCase().includes(query);
        const matchesSku = item.product_sku?.toLowerCase().includes(query);
        if (!matchesName && !matchesSku) return false;
      }

      // Variance filter
      if (varianceFilter === 'variance' && item.variance === 0) return false;
      if (varianceFilter === 'shortage' && (item.variance === null || item.variance >= 0)) return false;
      if (varianceFilter === 'overage' && (item.variance === null || item.variance <= 0)) return false;

      return true;
    });
  }, [varianceData, searchQuery, varianceFilter]);

  // Summary stats
  const summaryStats = useMemo(() => {
    if (!filteredVariance.length) {
      return {
        totalProducts: 0,
        productsWithVariance: 0,
        totalShortage: 0,
        totalOverage: 0,
        shortageValue: 0,
        overageValue: 0,
      };
    }

    const withVariance = filteredVariance.filter(v => v.variance !== 0);
    const shortages = filteredVariance.filter(v => (v.variance ?? 0) < 0);
    const overages = filteredVariance.filter(v => (v.variance ?? 0) > 0);

    return {
      totalProducts: filteredVariance.length,
      productsWithVariance: withVariance.length,
      totalShortage: shortages.reduce((sum, v) => sum + Math.abs(v.variance ?? 0), 0),
      totalOverage: overages.reduce((sum, v) => sum + (v.variance ?? 0), 0),
      shortageValue: shortages.reduce((sum, v) => sum + Math.abs(v.variance_value ?? 0), 0),
      overageValue: overages.reduce((sum, v) => sum + (v.variance_value ?? 0), 0),
    };
  }, [filteredVariance]);

  const isLoading = varianceLoading || overridesLoading;

  return (
    <div className="space-y-6" ref={printRef}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-semibold">Cashier Variance Report</h1>
          <p className="text-muted-foreground">
            Track inventory variance and stock override audit trail
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Date Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={varianceFilter} onValueChange={(v: any) => setVarianceFilter(v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                <SelectItem value="variance">With Variance</SelectItem>
                <SelectItem value="shortage">Shortages Only</SelectItem>
                <SelectItem value="overage">Overages Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Products Tracked
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summaryStats.totalProducts}</div>
            <p className="text-xs text-muted-foreground">
              {summaryStats.productsWithVariance} with variance
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Shortage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              -{summaryStats.totalShortage} units
            </div>
            <p className="text-xs text-muted-foreground">
              Value: {formatCurrency(summaryStats.shortageValue)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Overage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              +{summaryStats.totalOverage} units
            </div>
            <p className="text-xs text-muted-foreground">
              Value: {formatCurrency(summaryStats.overageValue)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Stock Overrides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {overrideLogs?.length || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Manager-approved sales
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="variance" className="gap-2">
            <Package className="h-4 w-4" />
            Inventory Variance
          </TabsTrigger>
          <TabsTrigger value="overrides" className="gap-2">
            <ShieldAlert className="h-4 w-4" />
            Stock Overrides ({overrideLogs?.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Variance Tab */}
        <TabsContent value="variance">
          <Card>
            <CardHeader>
              <CardTitle>Inventory Variance by Product</CardTitle>
              <CardDescription>
                Compare expected vs actual FOH stock levels
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredVariance.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No inventory data found for the selected period
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="text-right">Opening FOH</TableHead>
                        <TableHead className="text-right">Transfers In</TableHead>
                        <TableHead className="text-right">Transfers Out</TableHead>
                        <TableHead className="text-right">Sales</TableHead>
                        <TableHead className="text-right">Expected</TableHead>
                        <TableHead className="text-right">Actual</TableHead>
                        <TableHead className="text-right">Variance</TableHead>
                        <TableHead className="text-right">Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVariance.map((item) => (
                        <TableRow key={item.product_id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{item.product_name}</p>
                              {item.product_sku && (
                                <p className="text-xs text-muted-foreground">
                                  {item.product_sku}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {item.opening_foh}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-green-600">
                            {item.transfers_in > 0 ? `+${item.transfers_in}` : '-'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-blue-600">
                            {item.transfers_out > 0 ? `-${item.transfers_out}` : '-'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-red-600">
                            {item.sales_qty > 0 ? `-${item.sales_qty}` : '-'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {item.expected_closing}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-medium">
                            {item.actual_closing ?? '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {item.variance !== null && item.variance !== 0 ? (
                              <Badge
                                variant={item.variance < 0 ? 'destructive' : 'default'}
                                className={cn(
                                  item.variance > 0 && 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                )}
                              >
                                {item.variance > 0 ? '+' : ''}
                                {item.variance}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {item.variance_value !== null && item.variance_value !== 0 ? (
                              <span
                                className={cn(
                                  'font-medium',
                                  item.variance_value < 0 ? 'text-red-600' : 'text-green-600'
                                )}
                              >
                                {item.variance_value > 0 ? '+' : ''}
                                {formatCurrency(item.variance_value)}
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overrides Tab */}
        <TabsContent value="overrides">
          <Card>
            <CardHeader>
              <CardTitle>Stock Override Audit Trail</CardTitle>
              <CardDescription>
                Manager-approved sales when products were out of stock in FOH
              </CardDescription>
            </CardHeader>
            <CardContent>
              {overridesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : !overrideLogs || overrideLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShieldAlert className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No stock overrides recorded in this period</p>
                  <p className="text-sm mt-1">
                    Overrides are logged when a manager approves selling an out-of-stock item
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Cashier</TableHead>
                        <TableHead>Approved By</TableHead>
                        <TableHead className="text-right">FOH at Time</TableHead>
                        <TableHead className="text-right">Qty Sold</TableHead>
                        <TableHead>Reason</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {overrideLogs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">
                                {new Date(log.created_at).toLocaleString()}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {log.product_name}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {log.cashier_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              <ShieldAlert className="h-3 w-3" />
                              {log.manager_name}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="destructive">{log.foh_quantity_at_time}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {log.quantity_sold}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {log.override_reason || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
